import { useMemo } from 'react';
import { estimateStrength } from '@battini/crypto';
import { useApp } from '../store/AppContext.js';

export function SecurityDashboard() {
  const { items, security } = useApp();

  const analysis = useMemo(() => {
    const live = items.filter((i) => !i.deletedAt && i.password);
    const weak: string[] = [];
    const old: string[] = [];
    // Reuse detection groups identical decrypted passwords in memory. This runs
    // entirely client-side; no password value (or digest) is sent to the server.
    const byPw = new Map<string, string[]>();
    for (const i of live) {
      const score = estimateStrength(i.password ?? '').score;
      if (score < 2) weak.push(i.title);
      const ageDays = (Date.now() - new Date(i.updatedAt).getTime()) / 86_400_000;
      if (ageDays > 365) old.push(i.title);
      const arr = byPw.get(i.password!) ?? [];
      arr.push(i.title);
      byPw.set(i.password!, arr);
    }
    const reused = [...byPw.values()].filter((arr) => arr.length > 1).flat();
    return { total: live.length, weak, old, reused };
  }, [items]);

  const score = Math.max(0, 100 - analysis.weak.length * 10 - analysis.reused.length * 8 - analysis.old.length * 2 - (security?.mfaEnabled ? 0 : 15));

  return (
    <div>
      <div className="cards mb">
        <div className="stat success">
          <div className="n">{score}</div>
          <div className="l">Security score</div>
        </div>
        <div className="stat danger">
          <div className="n">{analysis.weak.length}</div>
          <div className="l">Weak passwords</div>
        </div>
        <div className="stat warn">
          <div className="n">{analysis.reused.length}</div>
          <div className="l">Reused passwords</div>
        </div>
        <div className="stat">
          <div className="n">{analysis.old.length}</div>
          <div className="l">Not updated in 1y</div>
        </div>
      </div>

      <div className="grid">
        {!security?.mfaEnabled && (
          <div className="alert warn">
            <strong>MFA is not enabled.</strong> Add a second factor from Settings → MFA to protect against credential theft.
          </div>
        )}
        {analysis.weak.length > 0 && (
          <div className="panel">
            <h3 style={{ marginTop: 0 }}>⚠️ Weak passwords</h3>
            <p className="muted" style={{ fontSize: 13 }}>Detected locally in your browser; no password value was sent to the server.</p>
            <ul>{analysis.weak.map((t) => <li key={t}>{t}</li>)}</ul>
          </div>
        )}
        {analysis.reused.length > 0 && (
          <div className="panel">
            <h3 style={{ marginTop: 0 }}>♻️ Reused passwords</h3>
            <p className="muted" style={{ fontSize: 13 }}>These items share a password. Use unique passwords per site.</p>
            <ul>{analysis.reused.map((t) => <li key={t}>{t}</li>)}</ul>
          </div>
        )}
        {analysis.old.length > 0 && (
          <div className="panel">
            <h3 style={{ marginTop: 0 }}>🕓 Old entries</h3>
            <p className="muted" style={{ fontSize: 13 }}>Not updated in over a year — consider rotating these.</p>
            <ul>{analysis.old.map((t) => <li key={t}>{t}</li>)}</ul>
          </div>
        )}
        <div className="panel">
          <h3 style={{ marginTop: 0 }}>🔎 Breach check</h3>
          <p className="muted" style={{ fontSize: 13 }}>
            Placeholder integration. When enabled, this will consult a k-anonymity breach API (SHA-1 prefix range
            query) so the full password hash is never transmitted.
          </p>
        </div>
        {analysis.weak.length === 0 && analysis.reused.length === 0 && analysis.old.length === 0 && (
          <div className="alert success">✓ No weak, reused, or stale passwords detected across {analysis.total} items.</div>
        )}
      </div>
    </div>
  );
}
